import * as React from 'react';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { brand } from '../theme';

export const SIDEBAR_WIDTH = 240;

interface NavItem {
  text: string;
  target: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const topSections: NavSection[] = [
  {
    title: 'Products',
    items: [
      { text: 'Plans', target: 'dashboard/plans' },
      { text: 'Disclaimers', target: 'dashboard/disclaimers' },
      { text: 'Routers', target: 'dashboard/routers' },
      { text: 'Terms', target: 'dashboard/terms' },
    ],
  },
  {
    title: 'Promotions',
    items: [
      { text: 'Campaigns', target: 'dashboard/campaigns' },
      { text: 'Coupons', target: 'dashboard/coupons' },
      { text: 'Commission Sales', target: 'dashboard/commission_sales' },
    ],
  },
  {
    title: 'Referrals',
    items: [
      { text: 'Referrals', target: 'dashboard/referrals' },
    ],
  },
  {
    title: 'Customers',
    items: [
      { text: 'Accounts', target: 'dashboard/accounts' },
      { text: 'Sign-up Stats', target: 'dashboard/signup-stats' },
      { text: 'Active Reservations', target: 'dashboard/active_reservations' },
      { text: 'Community Fibre orders', target: 'dashboard/cf-orders' },
    ],
  },
  {
    title: 'Buildings',
    items: [
      { text: 'Building Editor', target: 'dashboard/building-editor' },
    ],
  },
];

const bottomSection: NavSection = {
  title: 'Demo',
  items: [
    { text: 'CRN Reconciliation', target: 'dashboard/reconciliation/crn' },
  ],
};

const renderSection = (
  section: NavSection,
  currentPath: string,
) => (
  <List
    key={section.title}
    dense
    subheader={(
      <ListSubheader
        sx={{
          bgcolor: 'transparent',
          // 0.8 for 5.8:1 on the green; 0.6 was 4.0:1, short of the 4.5:1
          // small text needs.
          color: 'rgba(255,255,255,0.8)',
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          lineHeight: 2,
        }}
      >
        {section.title}
      </ListSubheader>
    )}
  >
    {section.items.map((item) => {
      const path = `/${item.target}`;
      const selected = currentPath === path || currentPath.startsWith(`${path}/`);
      return (
        // A real link, so a page can be opened in a new tab or middle-clicked
        // like any other link.
        <ListItemButton
          key={item.target}
          component={RouterLink}
          to={path}
          selected={selected}
          aria-current={selected ? 'page' : undefined}
          sx={{
            color: 'white',
            borderLeft: '3px solid transparent',
            '&.Mui-selected': {
              bgcolor: 'rgba(255,255,255,0.12)',
              borderLeftColor: brand.yellow,
            },
            '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            // MUI's own focus ring is a faint grey, invisible on the green.
            '&.Mui-focusVisible': {
              outline: `2px solid ${brand.yellow}`,
              outlineOffset: '-2px',
            },
          }}
        >
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: selected ? 600 : 400 }}
          />
        </ListItemButton>
      );
    })}
  </List>
);

const SideNav = () => {
  const location = useLocation();

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          background: brand.greenDark,
          color: 'white',
          borderRight: 0,
        },
      }}
    >
      <Toolbar />
      <Box sx={{
        display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto',
      }}
      >
        <Box sx={{ pt: '20px' }}>
          {topSections.map((s) => renderSection(s, location.pathname))}
        </Box>
        <Box sx={{ mt: 'auto' }}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
          {renderSection(bottomSection, location.pathname)}
        </Box>
      </Box>
    </Drawer>
  );
};

export default SideNav;
